import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ChartInput, ChartModule, ChartResult } from '../domain/types';
import { runChart } from '../engine/runChart';
import { ChartFormFields } from './ChartFormFields';
import { Icon } from './Icon';

function currentLocalDateTime(): string {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
}

export function ChartForm({ module, onResult }: { module: ChartModule; onResult: (result: ChartResult) => void }) {
  const [dateTime, setDateTime] = useState(() => module.form.requiresBirthDetails ? '' : currentLocalDateTime());
  const [question, setQuestion] = useState('');
  const [gender, setGender] = useState<'' | 'male' | 'female'>('');
  const [dateType, setDateType] = useState<'solar' | 'lunar'>('solar');
  const [isLeapMonth, setIsLeapMonth] = useState(false);
  const [method, setMethod] = useState<'time' | 'manual'>('time');
  const [lineValues, setLineValues] = useState([7, 8, 7, 8, 7, 8]);
  const [numberA, setNumberA] = useState(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const activeRef = useRef(true);
  const requestIdRef = useRef(0);
  const loadingRef = useRef(false);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      requestIdRef.current += 1;
    };
  }, []);

  const numberInput = module.form.numberInput;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (loadingRef.current) return;
    loadingRef.current = true;
    setError('');
    const requestId = ++requestIdRef.current;
    setLoading(true);
    const input: ChartInput = {
      moduleId: module.id,
      dateTime,
      question: question.trim(),
      gender: gender || undefined,
      dateType,
      isLeapMonth: dateType === 'lunar' ? isLeapMonth : false,
      lineValues: module.form.supportsManualLines && method === 'manual' ? lineValues : undefined,
      numbers: numberInput ? [numberA] : undefined
    };
    try {
      const nextResult = await runChart(input);
      if (activeRef.current && requestIdRef.current === requestId) onResult(nextResult);
    } catch (cause) {
      if (activeRef.current && requestIdRef.current === requestId) {
        setError(cause instanceof Error ? cause.message : '排盘失败，请检查输入后重试。');
      }
    } finally {
      if (activeRef.current && requestIdRef.current === requestId) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }

  return (
    <form className="chart-form" onSubmit={handleSubmit} aria-describedby="method-note">
      <div className="workspace-title">
        <div>
          <span className="eyebrow">{module.category}</span>
          <h2>{module.name}</h2>
          <p id="method-note">{module.description}</p>
        </div>
        <span className="method-source">依据：{module.tradition}</span>
      </div>

      <ChartFormFields
        module={module}
        dateTime={dateTime}
        question={question}
        gender={gender}
        dateType={dateType}
        isLeapMonth={isLeapMonth}
        method={method}
        lineValues={lineValues}
        numberValue={numberA}
        onDateTimeChange={setDateTime}
        onQuestionChange={setQuestion}
        onGenderChange={setGender}
        onDateTypeChange={(value) => {
          setDateType(value);
          if (value === 'solar') setIsLeapMonth(false);
        }}
        onLeapMonthChange={setIsLeapMonth}
        onMethodChange={setMethod}
        onLineValueChange={(index, value) => setLineValues((current) => current.map((item, itemIndex) => itemIndex === index ? value : item))}
        onNumberChange={setNumberA}
      />

      {error && <p className="form-error" role="alert">{error}</p>}
      <p className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">{loading ? '正在计算排盘，请稍候。' : ''}</p>
      <button className="primary-button" type="submit" aria-disabled={loading}>
        {loading ? <><span className="spinner" aria-hidden="true" />计算中…</> : <><Icon name="compass" />生成排盘</>}
      </button>
    </form>
  );
}
